import { writeModuleIndex } from "../shared/module-index.ts";
import { listPath } from "./paths.ts";
import type { RenderedTask } from "./render.ts";

/**
 * Disk-writing counterpart to render.ts's pure renderTaskListJson — kept
 * separate the same way Calendar and Contacts split their pure render logic
 * from their disk-writing pass, so render.ts itself stays unit-testable
 * under plain Node.
 */

export interface TaskListGroup {
    key: string;
    label: string;
    tasks: readonly RenderedTask[];
}

/**
 * Rebuilds `tasks/index.html` — one row per task across every list — from
 * the lists exportTasks already fetched and rendered this run. Each row
 * links to its whole list's JSON file (there is no separate per-task file).
 */
export async function renderTasksIndex(
    root: FileSystemDirectoryHandle,
    groups: readonly TaskListGroup[]
): Promise<void> {
    const rows = groups.flatMap(({ key, label, tasks }) =>
        tasks.map((task) => ({
            cells: {
                title: task.title || "(no title)",
                list: label,
                status: task.status,
                due: task.due ? new Date(task.due).toLocaleDateString() : ""
            },
            sortValues: { due: task.due ? Date.parse(task.due) || 0 : 0 },
            // Relative to tasks/index.html, which sits next to every list's
            // own JSON file — same directory, so the bare filename works.
            href: listPath(key).replace(/^tasks\//, "")
        }))
    );

    await writeModuleIndex(root, {
        moduleId: "tasks",
        itemKind: "data",
        title: "Tasks",
        subtitle: `${rows.length} task${rows.length === 1 ? "" : "s"} across ${groups.length} list${
            groups.length === 1 ? "" : "s"
        }`,
        columns: [
            { key: "title", label: "Title" },
            { key: "list", label: "List" },
            { key: "status", label: "Status" },
            { key: "due", label: "Due" }
        ],
        rows,
        emptyMessage: "No tasks exported yet.",
        searchPlaceholder: "Filter tasks…"
    });
}
